import { computed, onBeforeUnmount, readonly, ref } from 'vue'

import { substitute } from '@/composables/contentful'
import {
  AUTO_CLOSE_MS,
  AUTO_CLOSE_NON_REWARD_MS,
  CELEBRATION_CROSSFADE_MS,
  CELEBRATION_START_MS,
  CHECK_SWAP_MS,
  DRAWER_IN_MS,
  FILL_END_MS,
  FILL_START_MS,
  NODE_MAGENTA_MS,
  REWARD_ROW_ARRIVAL_MS,
  WARM_OPEN_SHIFT_MS
} from '@/lib/rewardSequenceTimings'

import { RewardCelebrationStage } from '@/components/Shared/RewardsTracker/RewardCelebrationStage'

import type { RewardTier } from '@/types/RewardTier'

import { cartSidebarContent } from '@/content/cartSidebar'

/**
 * The reward-unlock sequence, as an explicit state machine (Gap 15, strategy §3).
 *
 * **This is production code that the harness drives, never a harness implementation.** A second copy
 * living in the debug view would mean the beat sheet for design sign-off validates something other
 * than what ships, and a fix to one would silently miss the other.
 *
 * **Why a machine rather than nested timeouts.** The Figma Make prototype chains `setTimeout`s and
 * never clears them, so two overlapping adds double-schedule and the tracker fights itself. Mirroring
 * that structure inherits the bug. Here every scheduled beat goes through {@link schedule}, which
 * registers its handle, and every exit path clears the registry — so an interrupt cannot leave a beat
 * in flight from a sequence that is already over.
 *
 * **The input contract is what makes the harness possible.** {@link RewardSequenceInput} is a plain
 * value; the machine never subscribes to the cart. Production computes the descriptor from
 * `useRewardThresholdCrossing`; the harness passes the same shape built from synthetic tiers. Same
 * machine, same components, two callers.
 *
 * **Scope of A1.** States, contract, timer discipline and the single `settle()` path. The visual beats
 * hang off `phase` and `fillPct` at A2a, and the live crossing wiring is A2b.
 */

/** Sequence states, named for the spec's beats. */
export enum RewardSequencePhase {
  Idle = 'idle',
  Opening = 'opening',
  Filling = 'filling',
  Celebrating = 'celebrating',
  /** Collapsed to the end state early — by interaction, reduced motion, or an interrupt. */
  Settling = 'settling',
  Closing = 'closing'
}

/**
 * Which way the rail travels on this run.
 *
 * The `Filling` phase is named for the common case, but the same beat runs when a cart *shrinks* — a
 * removed item pulls the bar back. Anything describing the run to a person needs the direction, and it
 * cannot be read off `fillPct` at the moment the beat fires: the phase is assigned first and the new
 * target a statement later, so a synchronous observer sees only where the bar is leaving from.
 */
export enum RewardFillDirection {
  Unchanged = 'unchanged',
  Filling = 'filling',
  Emptying = 'emptying'
}

/**
 * Why a run collapsed to its end state early — see {@link RewardSequenceApi.settle}.
 *
 * It also decides whether the collapse animates, because the two have never varied independently: an
 * interruption still owes the customer the crossfade it cut into, and reduced motion is a request not
 * to see one. Carrying the reason rather than a bare `animate` flag means anything reporting the
 * ending can say *why* it ended, which a boolean could only guess at.
 */
export enum RewardSettleReason {
  /** The customer reached into the cart mid-sequence. */
  Interaction = 'interaction',
  /** `prefers-reduced-motion`, honoured through the same path rather than a parallel one. */
  ReducedMotion = 'reduced-motion'
}

/**
 * Everything the machine needs to run one sequence.
 *
 * `fromPct` / `toPct` are rail-fill percentages, not subtotals — the machine drives a bar, and keeping
 * money out of the contract is what stops it growing a dependency on the cart.
 */
export interface RewardSequenceInput {
  /** Every tier this mutation unlocked, ascending by threshold. May be empty for a non-reward add. */
  unlockedTiers: RewardTier[]
  /** Tracker fill before the mutation, 0–100. */
  fromPct: number
  /** Tracker fill after it, 0–100. */
  toPct: number
}

/**
 * The unlock announcement's copy.
 *
 * A multi-tier crossing celebrates one reward visually but has genuinely earned several, and a screen
 * reader user gets no other signal that the others arrived — so the count is stated. The "including"
 * phrasing matches the tracker's own all-unlocked heading rather than inventing a second voice.
 */
export function buildUnlockAnnouncement(unlockedTiers: RewardTier[]): string {
  const last = unlockedTiers[unlockedTiers.length - 1]
  if (!last) return ''

  const name = last.unlockedLabel ?? last.label
  return unlockedTiers.length === 1
    ? substitute(cartSidebarContent.rewardUnlockedAnnouncementOne, { RewardName: name })
    : substitute(cartSidebarContent.rewardUnlockedAnnouncementOther, {
        Count: unlockedTiers.length,
        RewardName: name
      })
}

export interface RewardSequenceStartOptions {
  /**
   * The cart was already on screen when the customer acted.
   *
   * It governs two things. There is no drawer to slide in, so every beat runs
   * {@link WARM_OPEN_SHIFT_MS} earlier — and **the sequence does not auto-close at the end**, because
   * a cart that was already open was opened by the customer and is not the sequence's to put away.
   */
  warmOpen?: boolean
  /**
   * Collapse straight to the end state instead of playing the beats.
   *
   * Passed in rather than read from `matchMedia` here, for two reasons: the machine stays free of
   * environment access, exactly as it stays free of store access; and the harness needs to force it
   * on to demonstrate it, which it could not do against a real media query.
   */
  reducedMotion?: boolean
}

export function useRewardSequence() {
  const phase = ref<RewardSequencePhase>(RewardSequencePhase.Idle)
  const fillPct = ref(0)

  /**
   * The tier the celebration is about — the **last** tier the mutation unlocked.
   *
   * A multi-tier crossing celebrates once, for the highest tier reached (business ruling 2026-08-05),
   * so this is a single tier rather than a queue. `unlockedTiers` still arrives in full because the
   * bar fills to the final position in one run and because a harness must be able to construct a
   * genuine multi-tier crossing — but nothing iterates it as a work list.
   */
  const celebratedTier = ref<RewardTier | null>(null)

  const isRunning = computed(() => phase.value !== RewardSequencePhase.Idle)

  /** Whether the sequence reached its end early, so A3 knows an auto-close is no longer owed. */
  const wasSettled = ref(false)

  /** Why it did, when it did. Null on a run that played out, and reset by every `start()`. */
  const settleReason = ref<RewardSettleReason | null>(null)

  /**
   * Whether the run was abandoned rather than finished — see {@link cancel}.
   *
   * Both flags exist because `Idle` is the resting state of three different endings, and by the time
   * anything observes it the state that distinguished them has already been cleared. An observer that
   * cannot tell "the sequence ran out" from "the customer closed the drawer" will describe one as the
   * other, which is how a debug log ends up asserting that a cart stayed open moments after the
   * customer shut it.
   */
  const wasCancelled = ref(false)

  /** Which way this run's rail travels — see {@link RewardFillDirection}. */
  const fillDirection = ref<RewardFillDirection>(RewardFillDirection.Unchanged)

  /**
   * How far the celebrating node has got. Separate from {@link phase} because the node's two beats sit
   * *inside* the filling/celebrating phases rather than delimiting them — at 1,200ms it takes the
   * magenta while keeping its reward icon, and only at 1,800ms does it swap to the checkmark.
   */
  const celebrationStage = ref<RewardCelebrationStage>(RewardCelebrationStage.None)

  /**
   * Whether the bar has been sent toward its target.
   *
   * **`phase` cannot answer this, and on a warm run it answers it wrongly.** `applyStartState()` assigns
   * `Filling` at t=0 when the cart is already open, because there is no drawer to slide in — so the
   * fill-start beat 400ms later re-assigns the same value and any observer watching the phase sees the
   * fill begin at 0. The bar does not move until the beat. Pairing this with {@link fillComplete} keeps
   * the two ends of the fill honest: a warm run otherwise reports a 1,200ms fill against an 800ms
   * constant, which reads as the constant being wrong rather than the observation.
   */
  const fillStarted = ref(false)

  /**
   * Whether the bar has reached its target.
   *
   * The fill is a CSS transition the machine only starts, so its completion is not otherwise a state
   * anything can observe — and on a run that unlocks nothing it is the **last thing that moves**, which
   * makes it the beat the ending hangs off. Modelling it explicitly is what lets the activity log stamp
   * it: without it, a non-reward run reads as one beat at 700ms and then a silent wait, so a reader
   * measures the dead time from the fill's *start* and overstates it by the 800ms the bar was moving.
   * That is precisely how the 2026-08-12 feedback arrived — a correct complaint carrying an inflated
   * number.
   *
   * It is a milestone of the run rather than a debug affordance; the log is simply its first consumer.
   */
  const fillComplete = ref(false)

  /**
   * Whether the reward row is shown yet.
   *
   * The row's data arrived with the cart response; this gates its *reveal* at the 4,000ms beat. Worth
   * being explicit that it is a presentation delay over data already held, not an arrival — the
   * distinction matters when the row's thumbnail is still cold, because then the beat reveals a row
   * that is present, on time, and empty.
   */
  const rewardRowVisible = ref(false)

  /**
   * Whether the reward card has moved on to the next locked reward — the *other* half of the 4,000ms
   * beat, which the spec states as "Reward added as a free line item in the cart. Reward card
   * crossfades (350ms) to show the next locked reward in the gradient card."
   *
   * Until it flips, the banner must keep presenting **the reward being unlocked**. Without this the
   * card follows the cart's subtotal, which advances the instant the response lands — so the
   * celebration announces "Score! You Unlocked a Free Gift" beside the name and image of a reward the
   * customer has *not* earned. The tracker nodes and the reward rows are both held for the same
   * reason; the banner was the one element that was not.
   */
  const cardAdvanced = ref(true)

  /**
   * What to announce to assistive technology when a reward unlocks (Gap 19).
   *
   * The celebration is otherwise **purely visual** — a bar filling, a node changing colour, a card
   * crossfading — none of which a screen reader conveys. The reward row that lands at 4,000ms is
   * readable, but only if the customer happens to navigate to it, and it does not say that anything
   * just *changed*.
   *
   * Set when the celebration is reached by either route — the beat, or `settle()` collapsing to it —
   * so reduced-motion and interrupted runs announce exactly like an animated one. Empty at rest, so a
   * live region bound to it says nothing until there is something to say.
   */
  const announcement = ref('')

  /**
   * Every tier **this mutation** unlocked, by id.
   *
   * A multi-tier crossing celebrates only the last one, but the others still cannot simply appear
   * unlocked at t=0: their nodes would sit checked while the bar has not yet reached them, and their
   * reward rows would drop into the cart before the sequence that is supposed to be announcing them
   * has started. So the whole set is held and resolves together — the last tier additionally getting
   * the magenta and the pop, which is what "celebrate the last reward" means in practice.
   *
   * Tiers unlocked by an *earlier* mutation are absent, and must be: they are already part of the
   * cart's resting state and holding them would animate history.
   */
  const runUnlockedTierIds = ref<string[]>([])

  /**
   * Those of {@link runUnlockedTierIds} whose nodes should still render pre-crossing. Emptied at the
   * checkmark beat, when the whole set resolves at once.
   */
  const heldTierIds = ref<string[]>([])

  /** The input currently being run, so `settle()` knows what the end state actually is. */
  let target: RewardSequenceInput | null = null
  /** Remembered with it, because a backward `seek()` has to rebuild the same run from zero. */
  let targetWarmOpen = false
  /** The same flag, reactively, so the named beats can shift with the run. */
  const targetWarmOpenReactive = ref(false)
  /** Where this run's bar actually started — an interrupt resumes mid-bar, not at `input.fromPct`. */
  let targetFromPct = 0

  /**
   * The state at t=0 of the current run. Called by `start()` and again by a backward {@link seek},
   * which cannot invert a beat's effect and so replays from the beginning instead.
   */
  function applyStartState(): void {
    if (!target) return

    fillPct.value = targetFromPct
    // Derived here rather than in `start()` so a backward `seek()`, which replays from zero through
    // this function, rebuilds it along with everything else.
    if (target.toPct > targetFromPct) {
      fillDirection.value = RewardFillDirection.Filling
    } else if (target.toPct < targetFromPct) {
      fillDirection.value = RewardFillDirection.Emptying
    } else {
      fillDirection.value = RewardFillDirection.Unchanged
    }
    celebratedTier.value = target.unlockedTiers[target.unlockedTiers.length - 1] ?? null
    celebrationStage.value = celebratedTier.value
      ? RewardCelebrationStage.Pending
      : RewardCelebrationStage.None
    runUnlockedTierIds.value = target.unlockedTiers.map((tier) => tier.promotionId)
    heldTierIds.value = [...runUnlockedTierIds.value]
    fillStarted.value = false
    fillComplete.value = false
    // A non-reward add has no withheld row, so there is nothing for the reveal beat to gate — and no
    // celebration, so the card was never holding anything either.
    rewardRowVisible.value = !celebratedTier.value
    cardAdvanced.value = !celebratedTier.value
    // Cleared per run so a live region re-announces an identical unlock rather than seeing no change.
    announcement.value = ''
    phase.value = targetWarmOpen ? RewardSequencePhase.Filling : RewardSequencePhase.Opening
  }

  // --- The clock ---------------------------------------------------------------------------------
  //
  // Beats run off one virtual clock rather than a `setTimeout` each. Timeouts cannot be paused,
  // rewound, or run at anything but real speed, and the harness needs all three: the beat sheet for
  // design sign-off has to land on named beats *exactly* and hold still there while a screenshot is
  // taken (strategy §5).
  //
  // The clock advances by measured deltas rather than by counting ticks, so throttling changes how
  // often beats are checked but not when they are due — which keeps the behaviour a backgrounded tab
  // already had under timeouts, rather than quietly making the sequence tab-visibility-dependent.

  interface ScheduledBeat {
    atMs: number
    run: () => void
    fired: boolean
  }

  const beats: ScheduledBeat[] = []
  const elapsedMs = ref(0)
  const isPaused = ref(false)
  /** 1 is real time. Below 1 is slow motion; the *JS* beats stretch — see the note on `setSpeed`. */
  const speed = ref(1)

  let tickHandle: number | undefined
  let lastTickAt = 0

  function schedule(delayMs: number, run: () => void): void {
    beats.push({ atMs: Math.max(0, delayMs), run, fired: false })
  }

  /** Fires every beat due at `elapsedMs`, in beat order, exactly once each. */
  function runDueBeats(): void {
    beats
      .filter((beat) => !beat.fired && beat.atMs <= elapsedMs.value)
      .sort((a, b) => a.atMs - b.atMs)
      .forEach((beat) => {
        beat.fired = true
        beat.run()
      })
  }

  function tick(): void {
    const now = performance.now()
    const delta = now - lastTickAt
    lastTickAt = now
    if (isPaused.value) return

    elapsedMs.value += delta * speed.value
    runDueBeats()
    if (beats.every((beat) => beat.fired)) stopClock()
  }

  function startClock(): void {
    stopClock()
    lastTickAt = performance.now()
    tickHandle = window.setInterval(tick, 16)
  }

  function stopClock(): void {
    if (tickHandle !== undefined) {
      window.clearInterval(tickHandle)
      tickHandle = undefined
    }
  }

  /**
   * Drops every pending beat and stops the clock — **without** rewinding it.
   *
   * `elapsedMs` belongs to the run, not to the schedule, and only {@link start} begins a new run. It
   * used to be zeroed here, which quietly made every mid-run transition misreport itself: `settle()`
   * clears before it assigns `Settling`, so an interrupt at 1,500ms stamped 0ms and the `Finished`
   * after it stamped 350ms — time running backwards in the one readout whose entire purpose is when
   * things happened.
   */
  function clearScheduled(): void {
    stopClock()
    beats.length = 0
    isPaused.value = false
  }

  /**
   * Runs one sequence.
   *
   * Calling this while a sequence is running is an **interrupt, not a queue**: the in-flight beats are
   * dropped and the new run starts from wherever the bar currently sits, so a second add mid-sequence
   * restarts smoothly instead of snapping backwards to the new descriptor's `fromPct`.
   */
  function start(input: RewardSequenceInput, options: RewardSequenceStartOptions = {}): void {
    const resumeFrom = isRunning.value ? fillPct.value : input.fromPct

    clearScheduled()
    // The one place a new timeline begins, and so the one place the clock rewinds. Every beat below is
    // scheduled at an absolute offset from here.
    elapsedMs.value = 0
    target = input
    targetWarmOpen = options.warmOpen ?? false
    targetWarmOpenReactive.value = targetWarmOpen
    targetFromPct = resumeFrom
    wasSettled.value = false
    settleReason.value = null
    wasCancelled.value = false
    applyStartState()

    const shift = options.warmOpen ? WARM_OPEN_SHIFT_MS : 0
    const at = (beatMs: number): number => beatMs - shift

    schedule(at(FILL_START_MS), () => {
      phase.value = RewardSequencePhase.Filling
      fillStarted.value = true
      // The bar's own CSS transition carries it from here; the machine only sets the target.
      fillPct.value = input.toPct
    })

    // Scheduled above the early return, because both paths fill. On a celebration it is a waypoint; on
    // a non-reward run it is the last beat, and the one the ending is measured from.
    schedule(at(FILL_END_MS), () => {
      fillComplete.value = true
    })

    // A non-reward add fills the bar and stops — no celebration. Bailing here rather than scheduling
    // beats that read `celebratedTier` as null is what keeps that path from depending on every
    // downstream beat's null-handling. `applyStartState` has already left the row visible, since a
    // non-reward add's row was never withheld.
    //
    // It still gets the same ending as any other run — see {@link scheduleEnding}. Note what that
    // means for the phase: it stays `Filling` from the fill through to the close rather than going
    // idle at `FILL_END_MS`. The run is not over while the drawer's fate is still pending, and an
    // interaction in that window has to be able to `settle()` and cancel the close.
    if (!celebratedTier.value) {
      scheduleEnding(at, options.warmOpen ?? false)
      finishStart(options.reducedMotion ?? false)
      return
    }

    schedule(at(NODE_MAGENTA_MS), () => {
      celebrationStage.value = RewardCelebrationStage.Magenta
    })

    schedule(at(CHECK_SWAP_MS), () => {
      celebrationStage.value = RewardCelebrationStage.Checked
      // The whole crossing resolves here, not just the star. On a single-tier crossing this is the
      // same thing; on a multi-tier one it is what stops the other nodes having been checked all along.
      heldTierIds.value = []
    })

    schedule(at(CELEBRATION_START_MS), () => {
      phase.value = RewardSequencePhase.Celebrating
      announcement.value = buildUnlockAnnouncement(input.unlockedTiers)
    })

    schedule(at(REWARD_ROW_ARRIVAL_MS), () => {
      rewardRowVisible.value = true
      cardAdvanced.value = true
    })

    scheduleEnding(at, options.warmOpen ?? false)
    finishStart(options.reducedMotion ?? false)
  }

  /**
   * How a run ends: the drawer either puts itself away or stays where it is.
   *
   * **Auto-close puts the cart back the way it was found.** It fires only when the cart was *closed*
   * when the customer acted — the sequence opened it to show them something, so it tidies up after
   * itself. A cart that was already open was opened by the customer, and taking it away from them
   * would be answering a question they did not ask (open questions, item 4: "a manually opened cart
   * never auto-closes").
   *
   * **That is now the whole rule.** It used to carry a second condition — something had to have
   * unlocked — so a non-reward add opened the cart and left it open. The business reversed that on
   * 2026-08-07: every add that opened the cart closes it again. Which is what lets both paths share
   * one ending instead of restating the condition on each.
   *
   * `warmOpen` already carried this information for the timings; it turns out to answer the
   * auto-close question too, which is why this needs no input of its own.
   *
   * **When it fires depends on what the run had to show.** Both endings dwell for the same
   * {@link SEQUENCE_END_DWELL_MS}, but they measure it from different beats: a celebration from the
   * 4,000ms card advance, a non-reward run from the fill it ended on. Sharing the *constant* rather than
   * the *time* is the correction the business asked for on 2026-08-12 — holding both at a flat 6,000
   * left a finished bar on screen for 4.5 seconds, more than twice the pause after a celebration.
   *
   * It reads `celebratedTier` rather than taking a parameter because `applyStartState()` has already set
   * it at both call sites, and a second argument saying the same thing could be passed wrongly.
   */
  function scheduleEnding(at: (beatMs: number) => number, warmOpen: boolean): void {
    const closeAtMs = at(celebratedTier.value ? AUTO_CLOSE_MS : AUTO_CLOSE_NON_REWARD_MS)

    if (warmOpen) {
      schedule(closeAtMs, endRun)
      return
    }

    schedule(closeAtMs, () => {
      phase.value = RewardSequencePhase.Closing
    })

    schedule(closeAtMs + DRAWER_IN_MS, endRun)
  }

  /**
   * The terminal transition of a run that played out — the one place `Idle` is reached by finishing
   * rather than by being abandoned. Both of `scheduleEnding`'s endings and `settle`'s tail land here.
   *
   * It exists because `Idle` asserts more than a phase: it asserts that no run is in flight, and the
   * celebration refs are part of that claim. They used to be cleared only in `cancel()`, so a run that
   * simply ended left `celebratedTier` set and `celebrationStage` on `Checked` until the *next* run's
   * `applyStartState()` overwrote them — and a run only starts when it has somewhere to play, so a
   * remove taken with the cart closed does not provide one.
   *
   * Both consumers read those refs ungated — `useCartFlyoutSequence` and the sandbox's own tracker
   * binding — so a `--celebrating` node outlived its run. Invisible while the tier stayed unlocked,
   * since that selector's node declarations match `--unlocked`'s exactly, which is why nothing showed
   * in review. A tier re-locked before the next run is where it surfaced: magenta and glowing under a
   * locked glyph and a grey label, a combination the design has no name for.
   *
   * Cleared before the phase flips, for the same reason `cancel()` sets `wasCancelled` first —
   * whatever describes the transition should already be true when an observer sees it.
   *
   * `cancel()` does not route through here. Abandoning a run tears down the clock and the target as
   * well, in an order its own note explains, and folding the two would put that ordering at risk to
   * save three lines.
   */
  function endRun(): void {
    celebratedTier.value = null
    celebrationStage.value = RewardCelebrationStage.None
    phase.value = RewardSequencePhase.Idle
  }

  /**
   * Common tail of both `start()` paths.
   *
   * Reduced motion takes the **same `settle()` transition** as a mid-sequence interaction rather than
   * a parallel "skip the animations" branch. That was a design commitment from the skeleton (strategy
   * §3) and it earns itself here: there is exactly one definition of "the end state", so a beat added
   * later cannot be honoured by the animated path and forgotten by the reduced one.
   *
   * Note what it inherits: `settle()` cancels the auto-close, so a reduced-motion run leaves the cart
   * open rather than closing itself at 6s. That is the accessible reading — a panel that vanishes on
   * its own is motion too — but it is a behavioural difference, not just a visual one.
   */
  function finishStart(reducedMotion: boolean): void {
    startClock()
    if (reducedMotion) settle(RewardSettleReason.ReducedMotion)
  }

  /** Freezes the sequence where it is. Beats already fired stay fired; the rest wait. */
  function pause(): void {
    isPaused.value = true
  }

  function resume(): void {
    isPaused.value = false
    lastTickAt = performance.now()
    if (tickHandle === undefined && beats.some((beat) => !beat.fired)) startClock()
  }

  /**
   * Moves the clock to `ms` and brings the state with it.
   *
   * Forward is cheap — fire whatever is now due. Backward is not, because a beat's effect is a state
   * assignment with no inverse, so rewinding means replaying from zero: reset to the start state, then
   * fast-forward. That is why {@link start} sets every piece of state explicitly rather than mutating
   * what was already there — it doubles as the reset this needs.
   *
   * Seeking pauses. Landing on a beat and then drifting off it would defeat the point, which is to
   * hold a named beat still while a screenshot is taken.
   */
  function seek(ms: number): void {
    if (!target) return

    const to = Math.max(0, ms)
    if (to < elapsedMs.value) {
      beats.forEach((beat) => {
        beat.fired = false
      })
      applyStartState()
    }

    elapsedMs.value = to
    runDueBeats()
    pause()
  }

  /**
   * Scales the JS beats. **CSS durations do not scale**, and that is a real limit rather than an
   * oversight: the fill's transition, the banner's crossfade and the node's colour change are declared
   * in stylesheets and inline styles, so at 0.25× the beats spread out while each transition still
   * runs at full speed and finishes early.
   *
   * So this is a tool for *reaching* a moment, not for watching motion in slow motion — which is what
   * {@link seek} is for, and why the beat sheet uses seek rather than a slow playback.
   */
  function setSpeed(multiplier: number): void {
    speed.value = Math.max(0.1, multiplier)
  }

  /**
   * Collapses the sequence to its end state immediately and cancels the auto-close.
   *
   * One path serves three cases — interaction mid-sequence, `prefers-reduced-motion` (A5), and an
   * interrupt — which is why it exists from the skeleton rather than being retrofitted. It is also a
   * genuine accessibility requirement: with the scrim up and the page behind inert, the flyout is the
   * only thing a customer *can* touch, so touching it has to resolve the sequence rather than leave
   * them waiting out six seconds of animation.
   *
   * The drawer is deliberately left **open**. Settling means "show me the end of this now", not
   * "dismiss it" — a customer who reached in to interact has not asked for the panel to leave.
   *
   * **Safe to call at any time**, including when nothing is running — the guard below makes it a
   * no-op then. That is what lets a caller wire it to a raw interaction event without first asking
   * whether a sequence is in flight.
   *
   * @param reason why the run is ending early. Also decides whether the collapse animates: reduced
   *   motion wants no transition window, an interruption still owes the crossfade it cut into.
   */
  function settle(reason: RewardSettleReason): void {
    if (!isRunning.value) return

    const animate = reason !== RewardSettleReason.ReducedMotion
    clearScheduled()

    // Jump to the end state rather than merely stopping. "Settle" means the customer sees where the
    // sequence was going — a half-filled bar and a row that never arrived would be worse than either
    // the animation or its absence.
    if (target) fillPct.value = target.toPct
    // Set alongside the other end-state flags, so settling out of a run whose bar was still travelling
    // records that it was sent home rather than leaving the milestones permanently unreached.
    fillStarted.value = true
    fillComplete.value = true
    if (celebratedTier.value) celebrationStage.value = RewardCelebrationStage.Checked
    heldTierIds.value = []
    rewardRowVisible.value = true
    cardAdvanced.value = true
    if (target) announcement.value = buildUnlockAnnouncement(target.unlockedTiers)

    wasSettled.value = true
    settleReason.value = reason
    phase.value = RewardSequencePhase.Settling

    // `Settling` is not instantaneous even when nothing further is scheduled: the crossfade the
    // celebration already started still has to land. Holding the phase for that window is what lets
    // A2a keep one set of transition rules instead of a separate "collapsed" variant.
    //
    // Scheduled from where the clock stands rather than from zero, because settling interrupts a run
    // rather than starting one — the beat is "350ms from now", and `now` is wherever the customer
    // reached in.
    schedule(elapsedMs.value + (animate ? CELEBRATION_CROSSFADE_MS : 0), endRun)
    // `clearScheduled()` above stopped the clock, so the beat just queued needs it running again.
    // Missing this left `settle()` stuck in `Settling` forever — a regression from moving the beats
    // off `setTimeout`, which used to fire without a clock, and invisible until A5 first called it.
    startClock()
  }

  /**
   * Abandons the sequence outright — no end state, no celebration. For the customer closing the
   * flyout by hand, where settling to a celebration they dismissed would be wrong.
   */
  function cancel(): void {
    // Both of these precede `clearScheduled()` on purpose. The flag has to be set before the phase so
    // a synchronous observer of `Idle` can tell why it got there — and the phase has to be set before
    // the clock is torn down, because `clearScheduled()` zeroes `elapsedMs` and an observer stamping
    // this transition should get the time the run actually stopped rather than 0.
    wasCancelled.value = true
    phase.value = RewardSequencePhase.Idle

    clearScheduled()
    // Safe here, and only here, because the transition above has already been observed. An abandoned
    // run leaves nothing to read a time off, so the clock goes back to rest rather than holding the
    // moment it was dropped.
    elapsedMs.value = 0
    target = null
    targetWarmOpen = false
    targetWarmOpenReactive.value = false
    fillDirection.value = RewardFillDirection.Unchanged
    celebratedTier.value = null
    celebrationStage.value = RewardCelebrationStage.None
    runUnlockedTierIds.value = []
    heldTierIds.value = []
    fillStarted.value = false
    fillComplete.value = false
    rewardRowVisible.value = true
    cardAdvanced.value = true
    announcement.value = ''
    wasSettled.value = false
    settleReason.value = null
  }

  // A sequence outliving its component is the prototype's bug in a different costume.
  onBeforeUnmount(clearScheduled)

  /**
   * The named beats, so the harness can offer jump-to-beat without restating the spec's numbers.
   *
   * Shifted to match the run in progress. The spec's numbers are cold-open times, and a warm open runs
   * everything {@link WARM_OPEN_SHIFT_MS} earlier — so seeking to a literal 1,200 on a warm run lands
   * 300ms past the beat it names. `drawer in` is dropped on a warm run because there is no drawer
   * slide to stand on.
   *
   * **It describes the loaded run, not the spec.** A run that unlocked nothing schedules none of the
   * celebration beats and closes at {@link AUTO_CLOSE_NON_REWARD_MS}, so listing the full spec against
   * it would offer four beats that seek to nothing and name a close 2,500ms after the one that happens.
   * That is the same class of defect A4 found in the warm-open shift, and it matters for the same
   * reason: a beat-sheet capture is only worth taking if the label on it is true.
   *
   * Before the first run there is no run to describe, and `seek()` no-ops without a target — so the
   * non-reward shape showing at rest costs nothing.
   */
  const namedBeats = computed<{ label: string; atMs: number }[]>(() => {
    const shift = targetWarmOpenReactive.value ? WARM_OPEN_SHIFT_MS : 0
    const celebrates = celebratedTier.value !== null
    const beatList = [
      { label: 'drawer in', atMs: DRAWER_IN_MS },
      { label: 'fill start', atMs: FILL_START_MS },
      ...(celebrates ? [{ label: 'node magenta', atMs: NODE_MAGENTA_MS }] : []),
      { label: 'fill complete', atMs: FILL_END_MS },
      ...(celebrates
        ? [
            { label: 'checkmark', atMs: CHECK_SWAP_MS },
            { label: 'celebration', atMs: CELEBRATION_START_MS },
            { label: 'reward row', atMs: REWARD_ROW_ARRIVAL_MS }
          ]
        : []),
      { label: 'auto-close', atMs: celebrates ? AUTO_CLOSE_MS : AUTO_CLOSE_NON_REWARD_MS }
    ]

    return beatList
      .filter((beat) => !(shift > 0 && beat.label === 'drawer in'))
      .map((beat) => ({ ...beat, atMs: beat.atMs - shift }))
  })

  return {
    phase: readonly(phase),
    fillPct: readonly(fillPct),
    celebratedTier: readonly(celebratedTier),
    celebrationStage: readonly(celebrationStage),
    fillStarted: readonly(fillStarted),
    fillComplete: readonly(fillComplete),
    rewardRowVisible: readonly(rewardRowVisible),
    cardAdvanced: readonly(cardAdvanced),
    announcement: readonly(announcement),
    runUnlockedTierIds: readonly(runUnlockedTierIds),
    heldTierIds: readonly(heldTierIds),
    wasSettled: readonly(wasSettled),
    settleReason: readonly(settleReason),
    wasCancelled: readonly(wasCancelled),
    fillDirection: readonly(fillDirection),
    /**
     * Whether the current run began with the cart already on screen. Exposed because it is the reason
     * behind the ending as well as an input to it — anything explaining why the drawer closed, or
     * didn't, needs it, and it is not recoverable from the phase alone.
     */
    warmOpen: readonly(targetWarmOpenReactive),
    isRunning,
    start,
    settle,
    cancel,
    // Beat control. Harness-facing today; nothing in production drives these.
    elapsedMs: readonly(elapsedMs),
    isPaused: readonly(isPaused),
    speed: readonly(speed),
    pause,
    resume,
    seek,
    setSpeed,
    /** The spec's named beats, so a screenshot can be labelled with the beat it is standing on. */
    namedBeats
  }
}
